import { USERS } from "@/config/resources";
import { useAdmin } from "@/hooks/admin";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CheckCircleOutline,
  PauseCircleOutline,
  PhoneAndroid,
  Shield,
} from "@mui/icons-material";
import {
  ListBase,
  RecordContextProvider,
  useListContext,
  useNotify,
  useRefresh,
} from "react-admin";
import { SupplierCreateUserModalAction } from "./actions/create-user";
import { SupplierEditUserButton } from "./actions/edit-user";
import { SupplierDeleteUserButton } from "./actions/delete-user";
import { CustomPagination } from "@/resources/shared/pagination";
import {
  AxiosError,
  TCommercial,
  TSupplierRole,
  getSupplierRoles,
  updateUser,
} from "@/api";
import useSWR from "swr";
import { useCallback, useMemo, useState } from "react";

// ---------------------------------------------------------------------------
// SupplierUsersList — distributor user roster
//
// Card grid replaces the legacy datagrid: every commercial gets a tile with
// an avatar, name + role, phone with copyable email, and an inline status
// toggle that hits PUT /api/suppliers/admin/users/:id (status flag). Edit
// and Delete continue to flow through the existing modal/confirm actions.
// ---------------------------------------------------------------------------

export const SupplierUsersList = ({ supplierId }: { supplierId: string }) => {
  const { hasPermission } = useAdmin();

  const { data: rolesData } = useSWR(
    supplierId ? ["supplier-roles", supplierId] : null,
    () => getSupplierRoles(supplierId, { page: 0, size: 1000 }),
    { revalidateOnFocus: false },
  );

  const rolesMap = useMemo(() => {
    if (!rolesData?.content) return new Map<string, string>();
    return new Map<string, string>(
      rolesData.content.map((role: TSupplierRole) => [
        role.id as string,
        role.name as string,
      ]),
    );
  }, [rolesData]);

  const canManageUsers = hasPermission("supplier.action.createSeller");

  return (
    <ListBase
      resource={USERS}
      filter={{ supplierId }}
      perPage={12}
      sort={{ field: "firstName", order: "ASC" }}
      disableSyncWithLocation
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <UsersListToolbar
          supplierId={supplierId}
          canManageUsers={canManageUsers}
        />
        <UsersGrid rolesMap={rolesMap} canManageUsers={canManageUsers} />
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CustomPagination />
        </Box>
      </Stack>
    </ListBase>
  );
};

// --- Toolbar ----------------------------------------------------------------

const UsersListToolbar = ({
  supplierId,
  canManageUsers,
}: {
  supplierId: string;
  canManageUsers: boolean;
}) => {
  const { total, isLoading } = useListContext<TCommercial>();
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
    >
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography
          sx={{
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontSize: "0.7rem",
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          Usuarios del distribuidor
        </Typography>
        {!isLoading && typeof total === "number" && (
          <Chip
            size="small"
            label={`${total} ${total === 1 ? "registro" : "registros"}`}
            sx={{ fontWeight: 600, height: 22 }}
          />
        )}
      </Stack>
      {canManageUsers && (
        <Box>
          <SupplierCreateUserModalAction />
          {/* supplierId reserved for callers that fetch a fresh record context */}
          <input type="hidden" value={supplierId} />
        </Box>
      )}
    </Stack>
  );
};

// --- Grid -------------------------------------------------------------------

const UsersGrid = ({
  rolesMap,
  canManageUsers,
}: {
  rolesMap: Map<string, string>;
  canManageUsers: boolean;
}) => {
  const { data, isLoading, total } = useListContext<TCommercial>();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
          gap: 2,
        }}
      >
        {Array.from({ length: 6 }).map((_, idx) => (
          <Skeleton
            key={idx}
            variant="rounded"
            height={144}
            sx={{ borderRadius: 2 }}
          />
        ))}
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return <EmptyState canManageUsers={canManageUsers} />;
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          lg: "repeat(3, 1fr)",
        },
        gap: 2,
      }}
    >
      {data.map((record, index) => (
        <RecordContextProvider key={String(record.id)} value={record}>
          <UserCard
            record={record}
            roleName={
              record.roleId ? rolesMap.get(record.roleId) ?? null : null
            }
            index={index}
            canManageUsers={canManageUsers}
          />
        </RecordContextProvider>
      ))}
      {total != null && total === 0 && (
        <EmptyState canManageUsers={canManageUsers} />
      )}
    </Box>
  );
};

// --- Card -------------------------------------------------------------------

type UserCardProps = {
  record: TCommercial;
  roleName: string | null;
  index: number;
  canManageUsers: boolean;
};

const UserCard = ({
  record,
  roleName,
  index,
  canManageUsers,
}: UserCardProps) => {
  const theme = useTheme();
  const fullName = [record.firstName, record.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const initials = getInitials(record.firstName, record.lastName, record.email);
  const avatarColor = pickAvatarColor(record.email ?? String(record.id ?? ""));

  const isActive = record.status !== false;
  const accent = isActive
    ? theme.palette.success.main
    : theme.palette.action.disabledBackground;

  return (
    <Card
      variant="outlined"
      sx={{
        position: "relative",
        overflow: "hidden",
        borderColor: "divider",
        transition: "border-color 160ms ease, transform 160ms ease",
        animation: "user-card-in 360ms ease both",
        animationDelay: `${Math.min(index, 11) * 35}ms`,
        opacity: isActive ? 1 : 0.86,
        "@keyframes user-card-in": {
          from: { opacity: 0, transform: "translateY(6px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "&:hover": {
          borderColor: "primary.main",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          backgroundColor: accent,
          transition: "background-color 160ms ease",
        }}
      />
      <CardContent
        sx={{
          p: 2,
          pl: 2.5,
          "&:last-child": { pb: 2 },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: avatarColor,
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.95rem",
              boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 3px ${avatarColor}33`,
            }}
          >
            {initials}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ mb: 0.25 }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
                title={fullName || "Sin nombre"}
              >
                {fullName || "Sin nombre"}
              </Typography>
              <StatusToggle record={record} disabled={!canManageUsers} />
            </Stack>

            <Tooltip title={record.email ?? ""} disableHoverListener={!record.email}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontFamily:
                    "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
                  fontSize: "0.78rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {record.email || "—"}
              </Typography>
            </Tooltip>

            {record.phone && (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ mt: 0.5, color: "text.secondary" }}
              >
                <PhoneAndroid sx={{ fontSize: "0.85rem", opacity: 0.6 }} />
                <Typography variant="body2" sx={{ fontSize: "0.82rem" }}>
                  {record.phone}
                </Typography>
              </Stack>
            )}

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 1.25 }}
            >
              <Chip
                size="small"
                icon={<Shield sx={{ fontSize: "0.85rem !important" }} />}
                label={roleName ?? "Sin rol"}
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  borderColor: roleName ? "primary.light" : "divider",
                  color: roleName ? "primary.main" : "text.secondary",
                }}
              />
              <Chip
                size="small"
                icon={
                  isActive ? (
                    <CheckCircleOutline sx={{ fontSize: "0.85rem !important" }} />
                  ) : (
                    <PauseCircleOutline sx={{ fontSize: "0.85rem !important" }} />
                  )
                }
                label={isActive ? "Activo" : "Inactivo"}
                color={isActive ? "success" : "default"}
                variant={isActive ? "filled" : "outlined"}
                sx={{ height: 22, fontSize: "0.72rem", fontWeight: 600 }}
              />
              <Box sx={{ flex: 1 }} />
              {canManageUsers && (
                <Stack direction="row" spacing={0.25}>
                  <SupplierEditUserButton />
                  <SupplierDeleteUserButton />
                </Stack>
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// --- Status toggle (PUT admin/users/:id with status flip) -------------------

const StatusToggle = ({
  record,
  disabled,
}: {
  record: TCommercial;
  disabled?: boolean;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const isActive = record.status !== false;

  const onToggle = useCallback(async () => {
    if (!record?.id || !record.supplierId) return;
    setLoading(true);
    try {
      await updateUser(String(record.id), {
        ...record,
        status: !isActive,
      });
      notify(
        !isActive ? "Usuario activado" : "Usuario desactivado",
        { type: "success" },
      );
      refresh();
    } catch (error: any) {
      let msg: string | undefined;
      if (error instanceof AxiosError) {
        msg = error.response?.data?.message;
      }
      notify(msg || "Error al cambiar el estado del usuario.", {
        type: "warning",
      });
    } finally {
      setLoading(false);
    }
  }, [record, isActive, notify, refresh]);

  if (loading) {
    return <CircularProgress size={16} sx={{ mr: 0.5 }} />;
  }

  return (
    <Tooltip title={isActive ? "Desactivar usuario" : "Activar usuario"}>
      <Box component="span" sx={{ display: "inline-flex" }}>
        <Switch
          size="small"
          checked={isActive}
          onChange={onToggle}
          disabled={disabled}
          inputProps={{ "aria-label": "Cambiar estado del usuario" }}
        />
      </Box>
    </Tooltip>
  );
};

// --- Empty state ------------------------------------------------------------

const EmptyState = ({ canManageUsers }: { canManageUsers: boolean }) => (
  <Card
    variant="outlined"
    sx={{
      gridColumn: "1 / -1",
      borderStyle: "dashed",
      borderColor: "divider",
    }}
  >
    <CardContent sx={{ py: 5, textAlign: "center" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Sin usuarios registrados
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.5, mb: canManageUsers ? 2 : 0 }}
      >
        Aún no hay usuarios para este distribuidor.
      </Typography>
      {canManageUsers && <SupplierCreateUserModalAction />}
    </CardContent>
  </Card>
);

// --- Helpers ----------------------------------------------------------------

// Deterministic 11-color palette indexed by a stable string hash. Picks
// from MUI-ish hues that read well on the existing dark surface.
const AVATAR_PALETTE = [
  "#7C4DFF",
  "#26A69A",
  "#FF7043",
  "#42A5F5",
  "#EF5350",
  "#66BB6A",
  "#AB47BC",
  "#FFA726",
  "#5C6BC0",
  "#26C6DA",
  "#EC407A",
] as const;

const hashString = (input: string): number => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const pickAvatarColor = (seed: string): string =>
  AVATAR_PALETTE[hashString(seed) % AVATAR_PALETTE.length];

const getInitials = (
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null,
): string => {
  const fi = (firstName ?? "").trim()[0];
  const li = (lastName ?? "").trim()[0];
  if (fi || li) return `${fi ?? ""}${li ?? ""}`.toUpperCase() || "?";
  const local = (email ?? "").split("@")[0];
  return (local[0] ?? "?").toUpperCase();
};
